import React, { useEffect, useMemo, useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Chip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Fab,
  AppBar,
  Toolbar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  Tooltip,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  AccountBalance,
  TrendingUp,
  CreditCard,
  Refresh,
  PhotoCamera,
} from '@mui/icons-material';
import { SelectChangeEvent } from '@mui/material';
import { useNavigate } from '@tanstack/react-router';
import { useAuthStore } from '../stores/auth-store';
import { useAccountsStore } from '../stores/accounts-store';
import { accountsService } from '../services/accounts-service';
import { httpClient } from '../services/http-client';
import { Account, AccountType, AccountWithBalance } from '../types/api';
import { InstitutionLabel } from '../components/InstitutionMark';
import { AddAccountDialog } from '../components/AddAccountDialog';
import { EditAccountDialog } from '../components/EditAccountDialog';
import { HoldingsBaselineDialog } from '../components/HoldingsBaselineDialog';
import { MainLayout } from '../components/MainLayout';
import { AccountNameCell } from '../components/AccountNameCell';
import { accountLabel } from '../utils/account-label';
import { formatCurrency } from '../utils/currency';
import { useBaseCurrency } from '../stores/currency-store';

interface AccountManagementPageProps {
  onLogout?: () => void;
  /**
   * Filters carried in from wherever the user came from — in practice the
   * dashboard's "View all N accounts", which would otherwise drop them into an
   * unfiltered table and make them re-narrow it by hand. Supplied by the route
   * from the URL, so the filtered view is also shareable and reload-safe.
   */
  initialTypeFilter?: AccountType;
  initialInstitutionQuery?: string;
}

export const AccountManagementPage: React.FC<AccountManagementPageProps> = ({
  onLogout,
  initialTypeFilter,
  initialInstitutionQuery,
}) => {
  const navigate = useNavigate();
  const { user, token, logout } = useAuthStore();
  const baseCurrency = useBaseCurrency();
  const {
    accounts,
    isLoading,
    error,
    setAccounts,
    setLoading,
    setError,
    removeAccount
  } = useAccountsStore();

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [accountToDelete, setAccountToDelete] = useState<AccountWithBalance | null>(null);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [accountToEdit, setAccountToEdit] = useState<AccountWithBalance | null>(null);
  // "Start from a snapshot" — investment accounts only.
  const [baselineAccount, setBaselineAccount] = useState<AccountWithBalance | null>(null);
  const [selectedOwnerIds, setSelectedOwnerIds] = useState<string[]>([]);
  // Seeded from the URL and then owned by the page, so the incoming filter is
  // a starting point the user can clear rather than a cage.
  const [typeFilter, setTypeFilter] = useState<AccountType | undefined>(initialTypeFilter);
  const [institutionQuery, setInstitutionQuery] = useState(initialInstitutionQuery ?? '');

  const handleLogout = () => {
    httpClient.setAuthToken(null);
    httpClient.clearStoredCredentials();
    logout();
    onLogout?.();
  };

  const loadAccounts = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await accountsService.getAccountsWithBalances();
      console.log('Loaded accounts with balances:', response);
      setAccounts(response.data || []);
    } catch (error: any) {
      console.error('Failed to load accounts:', error);
      setError(error.message || 'Failed to load accounts');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) {
      loadAccounts();
    }
  }, [token]);
  const ownerOptions = useMemo(() => {
    const map = new Map<string, string>();
    accounts.forEach((acc) => {
      if (acc.owner_user_id) {
        map.set(acc.owner_user_id, acc.owner_email || acc.owner_user_id);
      }
    });
    return Array.from(map.entries()).map(([id, email]) => ({ id, email }));
  }, [accounts]);

  const filteredAccounts = useMemo(() => {
    let list = accounts;
    if (selectedOwnerIds.length > 0) {
      list = list.filter(
        (acc) => acc.owner_user_id && selectedOwnerIds.includes(acc.owner_user_id),
      );
    }
    if (typeFilter) {
      list = list.filter((acc) => acc.account_type === typeFilter);
    }
    if (institutionQuery) {
      // Substring, case-insensitive: the same match the overview card used, so
      // arriving here from that card shows the same set of accounts.
      const needle = institutionQuery.toLowerCase();
      list = list.filter((acc) => acc.institution?.toLowerCase().includes(needle));
    }
    return list;
  }, [accounts, selectedOwnerIds, typeFilter, institutionQuery]);

  const handleOwnerFilterChange = (event: SelectChangeEvent<string[]>) => {
    const {
      target: { value },
    } = event;
    setSelectedOwnerIds(typeof value === 'string' ? value.split(',') : value);
  };

  const handleMyAccountsToggle = () => {
    if (!user?.id) return;
    if (selectedOwnerIds.length === 1 && selectedOwnerIds[0] === user.id) {
      setSelectedOwnerIds([]);
    } else {
      setSelectedOwnerIds([user.id]);
    }
  };

  const isFilterActive =
    selectedOwnerIds.length > 0 || !!typeFilter || institutionQuery !== '';

  const handleAddAccount = async (accountData: any) => {
    try {
      const response = await accountsService.createAccount(accountData);
      // Refresh accounts after adding
      loadAccounts();
      setAddDialogOpen(false);
    } catch (err: any) {
      throw new Error(err.message || 'Failed to create account');
    }
  };

  const handleEditAccount = async (id: string, updates: any) => {
    try {
      await accountsService.updateAccount(id, updates);
      // Refresh accounts after update
      loadAccounts();
      setEditDialogOpen(false);
      setAccountToEdit(null);
    } catch (err: any) {
      throw new Error(err.message || 'Failed to update account');
    }
  };

  /**
   * Persist an inline rename.
   *
   * Sends ONLY display_name. `name` is the institution's label and no user
   * action may overwrite it -- that is the whole reason the two are separate
   * columns, and sending both here would quietly undo it.
   */
  const handleRename = async (id: string, displayName: string) => {
    try {
      await accountsService.updateAccount(id, { display_name: displayName });
      loadAccounts();
    } catch (err: any) {
      setError(err.message || 'Failed to rename account');
    }
  };

  const handleDeleteClick = (account: AccountWithBalance) => {
    setAccountToDelete(account);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!accountToDelete) return;

    try {
      await accountsService.deleteAccount(accountToDelete.id);
      removeAccount(accountToDelete.id);
      setDeleteDialogOpen(false);
      setAccountToDelete(null);
    } catch (err: any) {
      setError(err.message || 'Failed to delete account');
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setAccountToDelete(null);
  };

  const openEditDialog = (account: AccountWithBalance) => {
    setAccountToEdit(account);
    setEditDialogOpen(true);
  };

  const handleAddDialogClose = () => {
    setAddDialogOpen(false);
  };

  const handleEditDialogClose = () => {
    setEditDialogOpen(false);
    setAccountToEdit(null);
  };

  const getAccountIcon = (type: AccountType) => {
    switch (type) {
      case 'Asset':
        return <AccountBalance color="success" />;
      case 'Debt':
        return <CreditCard color="error" />;
      case 'Investment':
        return <TrendingUp color="primary" />;
      default:
        return <AccountBalance />;
    }
  };

  const getAccountTypeColor = (type: AccountType) => {
    switch (type) {
      case 'Asset':
        return 'success';
      case 'Debt':
        return 'error';
      case 'Investment':
        return 'primary';
      default:
        return 'default';
    }
  };

  // A single account, in its own currency.
  const formatAccountAmount = (amount: string | undefined, currency: string) =>
    formatCurrency(amount, currency, { base: baseCurrency });

  /**
   * Total for one account type, in the base currency.
   *
   * Reason: this used to sum `current_amount` -- each account's NATIVE balance --
   * and label the result 'USD'. Adding US dollars to Canadian dollars produces a
   * number that is not a quantity of anything, and the label made it look
   * deliberate. `current_amount_base` is the same balance already converted at
   * the rate stored on each ledger line, which is the only summable form.
   */
  const totalForType = (type: AccountType) =>
    formatCurrency(
      filteredAccounts
        .filter((acc) => acc.account_type === type)
        .reduce((sum, acc) => sum + parseFloat(acc.current_amount_base || acc.current_amount || '0'), 0),
      baseCurrency,
      { base: baseCurrency }
    );

  const formatLastUpdate = (dateStr?: string) => {
    if (!dateStr) return 'Never';
    return new Date(dateStr).toLocaleDateString();
  };

  return (
    <MainLayout>
      <Container maxWidth="lg" sx={{ mt: 4, mb: 8 }}>
        <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h4" fontWeight="bold" gutterBottom>
              Accounts
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Manage your assets, debts, and investments
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => setAddDialogOpen(true)}
          >
            Add Account
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2, alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 240 }}>
            <InputLabel id="owner-filter-label">Filter by member</InputLabel>
            <Select
              labelId="owner-filter-label"
              multiple
              value={selectedOwnerIds}
              label="Filter by member"
              onChange={handleOwnerFilterChange}
              renderValue={(selected) => {
                if (selected.length === 0) {
                  return 'All members';
                }
                return selected
                  .map((value) => ownerOptions.find((opt) => opt.id === value)?.email || value)
                  .join(', ');
              }}
            >
              {ownerOptions.map((option) => (
                <MenuItem key={option.id} value={option.id}>
                  <Checkbox checked={selectedOwnerIds.indexOf(option.id) > -1} />
                  <ListItemText primary={option.email} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            variant={selectedOwnerIds.length === 1 && user?.id && selectedOwnerIds[0] === user.id ? 'contained' : 'text'}
            onClick={handleMyAccountsToggle}
          >
            Only my accounts
          </Button>
          {/* Filters arriving from the dashboard are shown, not applied
              silently: an invisible filter is indistinguishable from missing
              accounts, and both chips clear in one click. */}
          {typeFilter && (
            <Chip
              label={`Type: ${typeFilter}`}
              onDelete={() => setTypeFilter(undefined)}
              size="small"
            />
          )}
          {institutionQuery && (
            <Chip
              label={`Institution: ${institutionQuery}`}
              onDelete={() => setInstitutionQuery('')}
              size="small"
            />
          )}
        </Box>

        <Paper sx={{ width: '100%', overflow: 'hidden', border: '1px solid', borderColor: 'divider' }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Institution</TableCell>
                  <TableCell>Currency</TableCell>
                  <TableCell align="right">Balance</TableCell>
                  <TableCell>Last Updated</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                      <Typography>Loading accounts...</Typography>
                    </TableCell>
                  </TableRow>
                ) : filteredAccounts.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <Box sx={{ py: 4 }}>
                        <Typography variant="body1" color="text.secondary" gutterBottom>
                          {isFilterActive ? 'No accounts match the active filters' : 'No accounts found'}
                        </Typography>
                        {!isFilterActive && (
                          <>
                            <Typography variant="body2" color="text.secondary">
                              Add your first account to start tracking your finances
                            </Typography>
                            <Button
                              startIcon={<Add />}
                              onClick={() => setAddDialogOpen(true)}
                              variant="contained"
                              sx={{ mt: 2 }}
                            >
                              Add Your First Account
                            </Button>
                          </>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredAccounts.map((account) => (
                    <TableRow key={account.id} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          {getAccountIcon(account.account_type)}
                          <AccountNameCell
                            account={account}
                            onRename={(displayName) => handleRename(account.id, displayName)}
                            secondary={
                              <>
                                <Typography variant="body2" color="text.secondary">
                                  {account.description || 'No description'}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {/* A joint account has no single owner, so
                                      naming one is worse than saying nothing:
                                      it implies the other partner has no stake
                                      in an account they are equally on. */}
                                  {account.is_joint
                                    ? 'Held jointly'
                                    : `Owner: ${account.owner_email || 'Unknown'}`}
                                  {account.is_joint && (
                                    <Chip label="Joint" size="small" color="secondary" sx={{ ml: 1 }} />
                                  )}
                                  {!account.is_joint && account.owner_user_id && account.owner_user_id === user?.id && (
                                    <Chip label="You" size="small" color="primary" sx={{ ml: 1 }} />
                                  )}
                                </Typography>
                              </>
                            }
                          />
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={account.account_type}
                          color={getAccountTypeColor(account.account_type) as any}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      {/* Long institution names ellipsise instead of widening
                          the column and pushing the balance off screen. */}
                      <TableCell sx={{ maxWidth: 220 }}>
                        <InstitutionLabel
                          name={account.institution}
                          fallback="No institution"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip label={account.currency} size="small" />
                      </TableCell>
                      <TableCell align="right">
                        <Typography
                          variant="body1"
                          fontWeight="medium"
                          color={account.account_type === 'Debt' ? 'error.main' : 'inherit'}
                        >
                          {formatAccountAmount(account.current_amount, account.currency)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatLastUpdate(account.last_snapshot_date)}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        {account.account_type === 'Investment' && (
                          <Tooltip title="Start from a snapshot of holdings">
                            <IconButton
                              size="small"
                              onClick={() => setBaselineAccount(account)}
                            >
                              <PhotoCamera />
                            </IconButton>
                          </Tooltip>
                        )}
                        <IconButton
                          size="small"
                          onClick={() => openEditDialog(account)}
                          color="primary"
                        >
                          <Edit />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteClick(account)}
                          color="error"
                        >
                          <Delete />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        {/* Summary Stats */}
        <Box sx={{ mt: 4, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Paper sx={{ p: 2, flex: 1, minWidth: 200 }}>
            <Typography variant="h6" color="primary" gutterBottom>
              Total Assets
            </Typography>
            <Typography variant="h4">
              {totalForType('Asset')}
            </Typography>
          </Paper>
          <Paper sx={{ p: 2, flex: 1, minWidth: 200 }}>
            <Typography variant="h6" color="success.main" gutterBottom>
              Total Investments
            </Typography>
            <Typography variant="h4">
              {totalForType('Investment')}
            </Typography>
          </Paper>
          <Paper sx={{ p: 2, flex: 1, minWidth: 200 }}>
            <Typography variant="h6" color="error.main" gutterBottom>
              Total Debts
            </Typography>
            <Typography variant="h4">
              {totalForType('Debt')}
            </Typography>
          </Paper>
        </Box>

        {/* Floating Action Button */}
        <Fab
          color="primary"
          aria-label="add account"
          sx={{ position: 'fixed', bottom: 16, right: 16 }}
          onClick={() => setAddDialogOpen(true)}
        >
          <Add />
        </Fab>

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={deleteDialogOpen}
          onClose={handleDeleteCancel}
          aria-labelledby="delete-dialog-title"
          aria-describedby="delete-dialog-description"
        >
          <DialogTitle id="delete-dialog-title">
            Delete Account
          </DialogTitle>
          <DialogContent>
            <DialogContentText id="delete-dialog-description">
              Are you sure you want to delete "
              {accountToDelete ? accountLabel(accountToDelete) : ''}"?
              This action cannot be undone and will remove all associated snapshots and data.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleDeleteCancel}>Cancel</Button>
            <Button onClick={handleDeleteConfirm} color="error" variant="contained">
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        {/* Add Account Dialog */}
        <AddAccountDialog
          open={addDialogOpen}
          onClose={handleAddDialogClose}
          onSuccess={() => {
            // Ideally we just reload, but if the dialog returns the account we could add it
            loadAccounts();
            handleAddDialogClose();
          }}
        />

        {/* Snapshot onboarding for investment accounts */}
        <HoldingsBaselineDialog
          open={!!baselineAccount}
          account={baselineAccount}
          onClose={() => setBaselineAccount(null)}
          onChanged={loadAccounts}
        />

        {/* Edit Account Dialog */}
        <EditAccountDialog
          open={editDialogOpen}
          account={accountToEdit}
          onClose={handleEditDialogClose}
          onSuccess={() => {
            loadAccounts();
            handleEditDialogClose();
          }}
        />
      </Container>
    </MainLayout>
  );
};
